import { useState, useEffect } from "react";
import { Copy, Check, Sparkles, Smartphone, ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Logo } from "@/components/Logo";

export default function InviteLanding() {
  const [refCode, setRefCode] = useState("");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  // Official Play Store URL for DailyHisaab
  const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.asldigitalsolutions.dailyhisaab";

  useEffect(() => {
    // Task 1.2: Implement logic to grab the 'ref' from URL: ?ref=ABCD123
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");
    if (code) {
      setRefCode(code.toUpperCase());
    }
  }, []);

  const handleCopyAndDownload = () => {
    // Task 1.3: "Copy Code" button logic
    if (refCode) {
      navigator.clipboard.writeText(refCode);
      setCopied(true);
      toast({
        title: "Code Copied!",
        description: "Now install the app and paste this code during signup.",
      });

      // Delay redirect slightly so user sees the "Copied" state
      setTimeout(() => {
        window.location.href = PLAY_STORE_URL;
      }, 1500);
    } else {
      window.location.href = PLAY_STORE_URL;
    }
  };

  return (
    // Task 1.1: Mobile-optimized container
    <div className="min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614] flex flex-col items-center px-6 py-12 font-sans">

      {/* Branding */}
      <div className="mb-10">
        <Logo size="lg" />
      </div>

      {/* Main Content Card */}
      <div className="w-full max-w-md bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl border border-slate-100 dark:border-slate-800 p-8 text-center flex flex-col items-center">

        {/* Welcome Icon */}
        <div className="w-20 h-20 bg-amber-50 dark:bg-amber-900/20 rounded-3xl flex items-center justify-center mb-6 ring-4 ring-amber-50/50">
          <Sparkles className="h-10 w-10 text-amber-600" />
        </div>

        {/* Task 1.2: Welcome Message */}
        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-3">
          You're Invited!
        </h1>

        <p className="text-slate-500 dark:text-slate-400 font-medium mb-8 leading-relaxed">
          Start professional billing today with <span className="font-bold text-[#8B1A1A]">DailyHisaab</span>. Apply the invite code below to help your friend earn rewards!
        </p>

        {/* Task 1.3: Big Referral Code UI */}
        {refCode ? (
          <div className="w-full bg-slate-50 dark:bg-slate-800/50 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-6 mb-8">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
              Your Referral Code
            </p>
            <p className="text-4xl font-black text-[#8B1A1A] dark:text-red-400 tracking-[0.15em] font-mono">
              {refCode}
            </p>
          </div>
        ) : (
          <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/10 rounded-xl text-red-600 text-sm font-bold">
            No referral code detected. You can still download the app below!
          </div>
        )}

        {/* Task 1.4: Download Button */}
        <Button
          onClick={handleCopyAndDownload}
          className="w-full h-16 rounded-2xl text-xl font-black text-white shadow-xl shadow-red-900/30 active:scale-[0.98] transition-all flex items-center justify-center gap-3"
          style={{ background: "linear-gradient(135deg, #8B1A1A 0%, #631212 100%)" }}
        >
          {copied ? (
            <>
              <Check className="h-6 w-6" /> Copied! Opening Store...
            </>
          ) : (
            <>
              {refCode ? "Copy & Download" : "Download App"} <Download className="h-6 w-6" />
            </>
          )}
        </Button>

        {/* Task 1.5: Instructional Section */}
        <div className="mt-10 w-full text-left space-y-4 border-t border-slate-50 dark:border-slate-800 pt-8">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center mb-2">
            3 Steps to help your friend
          </p>
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-black text-slate-500 shrink-0">1</div>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Copy the code above</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-black text-slate-500 shrink-0">2</div>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Install from Play Store</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-black text-slate-500 shrink-0">3</div>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-300">Paste code during Signup</p>
          </div>
        </div>
      </div>

      {/* Device Restriction Info */}
      <div className="mt-12 flex flex-col items-center gap-2">
        <div className="flex items-center gap-2 text-slate-400 dark:text-slate-600">
          <Smartphone className="h-4 w-4" />
          <span className="text-xs font-bold uppercase tracking-widest">Android Mobile App</span>
        </div>
      </div>
    </div>
  );
}